/* Editorial Tenant Services — dark editorial menu of services pulled
   from the studio's /api/services. Fully theme-reactive. Replaces the
   prior re-export of the founder cream Services which clashed with the
   Editorial dark Layout. */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { ArrowRight } from "lucide-react";
import { EditorialSkeleton } from "./_EditorialSkeleton";

function startingPrice(svc) {
  if (Array.isArray(svc?.tiers) && svc.tiers.length > 0) {
    const prices = svc.tiers
      .map((t) => Number(t?.price))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length) return Math.min(...prices);
  }
  const flat = svc?.price_from ?? svc?.starting_price ?? svc?.price;
  const n = Number(flat);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatPrice(n) {
  if (n == null) return null;
  return Number.isInteger(n) ? `$${n.toLocaleString("en-US")}` : `$${n.toFixed(2).replace(/\.00$/, "")}`;
}

export default function EditorialServices() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState([]);

  useEffect(() => {
    document.title = `${studioName} — Services`;
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, [studioName]);

  // Only real services from the studio's /api/services feed the menu.
  // The previous code fell back to a hard-coded PLACEHOLDERS array —
  // "Editorial Portrait Sitting · $1,400 / Brand Story Half-Day · $2,200
  // / Milestone Session · $3,200" — which Apex Studio (and every other
  // new tenant) saw as if those were their own published prices,
  // because the placeholders had real dollar amounts and Reserve CTAs.
  // Tenants with an empty service list now get a quiet empty-state
  // below the menu instead.
  const list = useMemo(() => services, [services]);
  const hasServices = list.length > 0;

  // Skeleton during the /api/site fetch — without it, first-time visitors
  // would see "Studio" as the H1 and the PLACEHOLDER services before the
  // real data arrives. Placed AFTER all hooks to honor Rules of Hooks.
  if (loading && !settings) {
    return <EditorialSkeleton eyebrow="Loading the menu." />;
  }

  return (
    <div
      data-testid="editorial-tenant-services"
      className="min-h-screen pt-28 pb-24"
      style={{ background: "var(--tenant-bg, #0A0908)", color: "var(--tenant-fg, #F5F2EC)" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        {/* Chapter masthead */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            Chapter II · Services
          </div>
          <div
            className="mt-5 h-px"
            style={{ width: "2rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <h1
            className="mt-10 leading-[0.95] tracking-tight"
            style={{
              fontFamily: "var(--tenant-serif)",
              fontWeight: 300,
              fontSize: "clamp(2.75rem, 7vw, 5.5rem)",
            }}
          >
            A short menu, kept by{" "}
            <em style={{ fontStyle: "italic", color: "var(--tenant-accent)" }}>hand</em>.
          </h1>
          <p
            className="mt-8 text-lg italic max-w-xl"
            style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
          >
            Considered sessions, each priced honestly. By appointment only.
          </p>
        </div>

        {/* Editorial preface — extended prose about how we price, what's
            standard across every booking, and who the menu is built for.
            Pulls from settings.services_intro / services_approach with
            editorial-voice fallbacks so any tenant has substantive copy. */}
        <div
          className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 max-w-[1240px]"
          data-testid="editorial-services-intro"
        >
          <div>
            <p
              className="text-lg leading-relaxed"
              style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.8))" }}
            >
              {settings?.services_intro
                || `The menu below is short on purpose. ${studioName} takes on a deliberately small number of editorial sittings each year — enough work to keep the studio sharp, few enough that each booking gets full attention from brief through to printed delivery. The four formats here are the ones we know inside out; they're the way we'd recommend a friend commission a session if they didn't know where to start.`}
            </p>
            <p
              className="mt-6 leading-relaxed"
              style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
            >
              {settings?.services_approach
                || `Prices are quoted as starting figures — they cover the photographer's time, post-production, a curated edit, and the digital delivery file. Wardrobe consultation, location scouting, and print credits are included as noted on each package. We don't run upsell tiers. If a particular sitting needs something the menu doesn't list (a studio rental, a second photographer, a video pass), we quote it at-cost on the call.`}
            </p>
            <p
              className="mt-6 leading-relaxed"
              style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
            >
              {settings?.services_reservation
                || `Every reservation is held with a small deposit and confirmed by a written agreement we send via email. Out-of-region commissions are accepted on a case-by-case basis; travel is billed transparently and never marked up.`}
            </p>
          </div>

          {/* Included-in-every-sitting card — works as the trust-signal panel. */}
          <aside
            className="border p-7 lg:p-9"
            style={{
              background: "var(--tenant-muted, rgba(245,242,236,0.04))",
              borderColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))",
            }}
            data-testid="editorial-services-included"
          >
            <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
              Included in every sitting
            </div>
            <div
              className="mt-3 mb-6 h-px"
              style={{ width: "2rem", background: "var(--tenant-accent)" }}
              aria-hidden
            />
            <ul className="space-y-4 leading-relaxed" style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.85))" }}>
              {[
                ["Pre-session call.", "A short call about wardrobe, mood, and the shape of the morning."],
                ["Wardrobe consultation.", "A written brief naming the looks that flatter the light and the room."],
                ["Editorial edit.", "Cinema-graded conversions delivered as high-res files."],
                ["Print credit.", "A studio print credit included on every booking above the portrait tier."],
                ["Private viewing room.", "A passworded gallery for downloads and album decisions, kept online for a year."],
                ["Archive copy.", "Originals kept in two locations for the working life of the studio."],
              ].map(([label, body], i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-x-3">
                  <span style={{ color: "var(--tenant-accent)", fontFamily: "var(--tenant-serif)", fontStyle: "italic" }}>·</span>
                  <span>
                    <span style={{ fontFamily: "var(--tenant-serif)", fontStyle: "italic", color: "var(--tenant-fg, #F5F2EC)" }} className="text-lg">
                      {label}
                    </span>{" "}
                    <span style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}>{body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        {/* Editorial 3-column menu — rendered only when the studio has
            real services; otherwise we show a quiet empty-state below.
            Avoids shipping hard-coded "starting at $1,400" placeholders
            to brand-new tenants who haven't published their menu yet. */}
        {hasServices ? (
        <div className="mt-20 lg:mt-28 grid grid-cols-1 md:grid-cols-3 gap-y-16 gap-x-0">
          {list.map((s, i) => {
            const price = startingPrice(s);
            const priceLabel = formatPrice(price);
            return (
              <div
                key={s.id || s.slug || i}
                data-testid={`editorial-service-card-${i}`}
                className={`px-6 lg:px-10 ${i % 3 !== 0 ? "md:border-l" : ""}`}
                style={i % 3 !== 0 ? { borderLeftColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" } : undefined}
              >
                <div
                  className="font-extralight tabular-nums leading-none"
                  style={{
                    fontFamily: "var(--tenant-serif)",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    color: "var(--tenant-accent)",
                    opacity: 0.8,
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3
                  className="mt-6 leading-tight"
                  style={{
                    fontFamily: "var(--tenant-serif)",
                    fontWeight: 400,
                    fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
                  }}
                >
                  {s.name || s.title}
                </h3>
                <div
                  className="mt-5 mb-5 h-px"
                  style={{ width: "2rem", background: "var(--tenant-accent)" }}
                  aria-hidden
                />
                <p
                  className="text-base leading-relaxed"
                  style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
                >
                  {s.description || s.summary}
                </p>
                <p
                  className="mt-6 text-lg italic"
                  style={{ fontFamily: "var(--tenant-serif)" }}
                  data-testid={`editorial-service-price-${i}`}
                >
                  {priceLabel ? (
                    <>
                      starting at{" "}
                      <span style={{ color: "var(--tenant-accent)" }}>{priceLabel}</span>
                    </>
                  ) : (
                    <span style={{ color: "var(--tenant-fg-mute, rgba(245,242,236,0.45))" }}>by request</span>
                  )}
                </p>
                <Link
                  to={tenantHref(`/booking?service=${s.slug || s.id || ""}`)}
                  className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] pb-1 border-b transition-colors hover:opacity-70"
                  style={{
                    color: "var(--tenant-accent)",
                    borderBottomColor: "var(--tenant-accent)",
                  }}
                  data-testid={`editorial-service-reserve-${i}`}
                >
                  Reserve <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            );
          })}
        </div>
        ) : (
          /* Empty-state — "menu is being prepared" — used when the
             studio hasn't published any services yet. Reads as
             intentional editorial copy (italic serif, gold accent),
             not as an error or missing data, and routes the visitor
             to Contact for an enquiry. */
          <div
            className="mt-20 lg:mt-28 max-w-xl mx-auto text-center"
            data-testid="editorial-services-empty"
          >
            <div
              className="font-extralight tabular-nums leading-none mx-auto"
              style={{
                fontFamily: "var(--tenant-serif)",
                fontSize: "clamp(2rem, 4vw, 3rem)",
                color: "var(--tenant-accent)",
                opacity: 0.65,
              }}
            >
              —
            </div>
            <h3
              className="mt-6 leading-tight"
              style={{
                fontFamily: "var(--tenant-serif)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "clamp(1.5rem, 2.4vw, 1.875rem)",
              }}
            >
              The menu is being prepared.
            </h3>
            <div
              className="mt-5 mb-5 h-px mx-auto"
              style={{ width: "2rem", background: "var(--tenant-accent)" }}
              aria-hidden
            />
            <p
              className="text-base leading-relaxed"
              style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
            >
              {studioName} is curating this season's sittings. Please
              write in for current availability and a tailored quote.
            </p>
            <Link
              to={tenantHref("/contact")}
              className="mt-8 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] pb-1 border-b transition-colors hover:opacity-70"
              style={{
                color: "var(--tenant-accent)",
                borderBottomColor: "var(--tenant-accent)",
              }}
              data-testid="editorial-services-empty-cta"
            >
              Enquire <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}
        <div
          className="mt-24 pt-10 border-t flex items-center justify-between gap-6 flex-wrap"
          style={{ borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
        >
          <span
            className="text-base italic"
            style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}
          >
            by appointment only.
          </span>
          <Link
            to={tenantHref("/booking")}
            data-testid="editorial-services-book-cta"
            className="inline-flex items-center gap-3 px-7 h-12 text-[10px] uppercase tracking-[0.38em] transition-colors"
            style={{
              background: "var(--tenant-fg, #F5F2EC)",
              color: "var(--tenant-bg, #0A0908)",
            }}
          >
            Reserve a sitting <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* How a session unfolds — 3-step process explainer.
            Adds substantive copy + sets expectations for first-time clients. */}
        <section
          className="mt-24 lg:mt-32 pt-14 border-t"
          style={{ borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
          data-testid="editorial-services-how-it-unfolds"
        >
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            How a session unfolds
          </div>
          <div
            className="mt-4 mb-7 h-px"
            style={{ width: "2rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <h2
            className="leading-[1.05] tracking-tight max-w-3xl"
            style={{
              fontFamily: "var(--tenant-serif)",
              fontWeight: 300,
              fontSize: "clamp(2rem, 4.2vw, 3.25rem)",
            }}
          >
            From the first letter to the printed plate.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-y-12 gap-x-0">
            {[
              {
                n: "01",
                title: "Letter & brief.",
                body:
                  "After you reserve, we trade a few notes by email about what you'd like remembered — the room, the wardrobe, the people in the frame. A pre-session call follows in the week before the booking to nail down call-times, the look, and any logistical knots.",
              },
              {
                n: "02",
                title: "The sitting.",
                body:
                  "On the day, we arrive twenty minutes before the call-time to set the room and unpack the kit. The actual photography is unhurried; there's no choreography sheet, and the morning is shaped around what's working in the light rather than what's been planned on paper.",
              },
              {
                n: "03",
                title: "The edit & delivery.",
                body:
                  "The cinema-graded set arrives within two weeks as a private viewing room — passworded, downloadable, kept online for a year. Print orders and album builds run on a separate timeline. Originals are archived to two locations for the working life of the studio.",
              },
            ].map((step, i) => (
              <div
                key={step.n}
                className={`px-6 lg:px-10 ${i % 3 !== 0 ? "md:border-l" : ""}`}
                style={i % 3 !== 0 ? { borderLeftColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" } : undefined}
                data-testid={`editorial-services-step-${i}`}
              >
                <div
                  className="font-extralight tabular-nums leading-none"
                  style={{
                    fontFamily: "var(--tenant-serif)",
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    color: "var(--tenant-accent)",
                    opacity: 0.8,
                  }}
                >
                  {step.n}
                </div>
                <h3
                  className="mt-6 leading-tight"
                  style={{
                    fontFamily: "var(--tenant-serif)",
                    fontWeight: 400,
                    fontSize: "clamp(1.4rem, 2.2vw, 1.75rem)",
                  }}
                >
                  {step.title}
                </h3>
                <div
                  className="mt-5 mb-5 h-px"
                  style={{ width: "2rem", background: "var(--tenant-accent)" }}
                  aria-hidden
                />
                <p
                  className="leading-relaxed"
                  style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.7))" }}
                >
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing note — bespoke commissioning prompt. */}
        <section
          className="mt-20 lg:mt-24 pt-12 border-t max-w-3xl mx-auto text-center"
          style={{ borderTopColor: "var(--tenant-fg-line, rgba(245,242,236,0.15))" }}
          data-testid="editorial-services-closing-note"
        >
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            A note on bespoke commissions
          </div>
          <div
            className="mt-4 mb-6 mx-auto h-px"
            style={{ width: "2rem", background: "var(--tenant-accent)" }}
            aria-hidden
          />
          <p
            className="leading-relaxed"
            style={{ color: "var(--tenant-fg-soft, rgba(245,242,236,0.75))" }}
          >
            If none of the formats above fits — a multi-day campaign, a book project, a printed annual report, a destination wedding — write to us with a one-paragraph brief and we'll come back with a tailored proposal within two business days. We take on a small number of bespoke commissions each year and price them per-project rather than per-day.
          </p>
        </section>
      </div>
    </div>
  );
}
